import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from 'components/common/LoadingButton';
import {
  FEE_TYPE_FIXED,
  FEE_TYPE_PERCENTAGE
} from 'components/donations/donation-settings/TransactionFees';
import { useTransactionFees } from 'hooks/useDonationSettings';
import AuthContext from 'context/authContext';
import PropTypes from 'prop-types';
import { useContext, useMemo, useState, useRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ADAdvancedSettings from './donation-form-components/ADAdvancedSettings';
import ADDonationDetails from './donation-form-components/ADDonationDetails';
import ADSelectDonor from './donation-form-components/ADSelectDonor';
import ADSelectedAppeals from './donation-form-components/ADSelectedAppeals';
import { Card, Col, Form, Nav, Row } from 'react-bootstrap';
import useScrollSpy from 'react-use-scrollspy';

// Side navigation links for the donation configuration
const sideNavLinks = [
  { id: 1, href: '#details', title: 'Donation Details' },
  { id: 2, href: '#appeals', title: 'Selected Appeals' },
  { id: 3, href: '#settings', title: 'Advanced Settings' }
];

export const calculatePercentageFee = (amount, percentage) => {
  return (amount * percentage) / 100;
};

const DonationConfig = ({ onSubmit, loading }) => {
  const { organization } = useContext(AuthContext);
  const [stickyStatus, setStickyStatus] = useState(false);

  // Set up scroll spy for navigation
  const sectionRefs = [useRef(null), useRef(null), useRef(null)];
  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -150
  });

  useEffect(() => {
    window.addEventListener('scroll', () => {
      if (window.scrollY < 100) {
        setStickyStatus(false);
      } else {
        setStickyStatus(true);
      }
    });

    return () => {
      window.removeEventListener('scroll', () => {});
    };
  }, []);

  const [selectedAppeal, setSelectedAppeal] = useState();
  const [index, setIndex] = useState();
  const [donationItems, setDonationItems] = useState([]);
  const { transactionFees: transactionFeesData } = useTransactionFees();
  const [searchParams] = useSearchParams();
  const donorId = searchParams.get('donorId');

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      donorId: donorId,
      paymentMethod: 'stripe',
      settings: {
        coverTransactionFee: false,
        giftAid: false
      }
    },
    resolver: yupResolver(
      Yup.object().shape({
        donorId: Yup.string().required('Required Field')
      })
    )
  });

  const { handleSubmit: RHFHandleSubmit, watch, setValue } = methods;
  const coverTransactionFee = watch('settings.coverTransactionFee');
  // useEffect(() => {
  //   if (donorId) {
  //     setValue('donorId', donorId);
  //   }
  // }, [donorId, setValue]);

  const handleSubmit = data => {
    const isOffline = [
      'cash',
      'bank_transfer',
      'cheque',
      'amember',
      'direct_debit'
    ].includes(data.paymentMethod);
    const transactionFees =
      !isOffline && data.settings.coverTransactionFee ? totalTransactionFee : 0;
    if (donationItems.length === 0) {
      toast.error('Please add a package');
    } else
      onSubmit({
        ...data,
        donationItems,
        subtotal: totalDonationAmount,
        transactionFees,
        total: totalDonationAmount + transactionFees
      });
  };

  const totalDonationAmount = useMemo(() => {
    return donationItems.reduce((acc, curr) => {
      acc += curr.amount * (curr.quantity || 1);
      return acc;
    }, 0);
  }, [donationItems]);
  const totalTransactionFee = useMemo(() => {
    if (!transactionFeesData || transactionFeesData.length === 0) return 0;

    return transactionFeesData.reduce((acc, curr) => {
      if (curr.fee_type === FEE_TYPE_PERCENTAGE) {
        acc = calculatePercentageFee(totalDonationAmount, curr.amount);
      } else if (curr.fee_type === FEE_TYPE_FIXED) {
        acc += curr.amount;
      }
      return acc;
    }, 0);
  }, [transactionFeesData, totalDonationAmount, coverTransactionFee]);

  const onAddNewDonationItem = data => {
    if (index === undefined) {
      setDonationItems(prevState => {
        const newState = [...prevState];
        newState.push(data);
        return newState;
      });
    } else {
      setDonationItems(prevState => {
        const newState = [...prevState];
        newState[index] = data;
        return newState;
      });
      setIndex(null);
      setSelectedAppeal(null);
    }
  };

  const onRemoveDonationItem = index => {
    setDonationItems(prevState => {
      const newState = [...prevState];
      newState.splice(index, 1);
      return newState;
    });
  };

  const onQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) return;
    setDonationItems(prevState => {
      const newState = [...prevState];
      newState[index] = { ...newState[index], quantity: newQuantity };
      return newState;
    });
  };

  const onEditDonationItemClick = index => {
    setSelectedAppeal(donationItems[index]);
    setIndex(index);
  };

  return (
    <Row className="g-3">
      <Col md={9}>
        <FormProvider {...methods}>
          <div ref={sectionRefs[0]}>
            <ADSelectDonor donorId={donorId} id="details" />
          </div>
          <div>
            <ADDonationDetails
              data={selectedAppeal?.values}
              onSubmit={onAddNewDonationItem}
            />
          </div>
          <div ref={sectionRefs[1]}>
            <ADSelectedAppeals
              items={donationItems}
              subtotal={totalDonationAmount}
              total={
                totalDonationAmount +
                (coverTransactionFee ? totalTransactionFee : 0)
              }
              transactionFee={coverTransactionFee ? totalTransactionFee : 0}
              onEdit={onEditDonationItemClick}
              onRemove={onRemoveDonationItem}
              onQuantityChange={onQuantityChange}
              id="appeals"
            />
          </div>
          <Card className="mb-3">
            <Card.Header className="pb-0">
              <Card.Title className="fw-medium">Payment Method</Card.Title>
            </Card.Header>
            <Card.Body>
              <Form.Check
                type="radio"
                id="pm-stripe"
                label="Card (Stripe)"
                value="stripe"
                checked={watch('paymentMethod') === 'stripe'}
                onChange={() => setValue('paymentMethod', 'stripe')}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="pm-cash"
                label="Cash"
                value="cash"
                checked={watch('paymentMethod') === 'cash'}
                onChange={() => setValue('paymentMethod', 'cash')}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="pm-bank-transfer"
                label="Bank Transfer"
                value="bank_transfer"
                checked={watch('paymentMethod') === 'bank_transfer'}
                onChange={() => setValue('paymentMethod', 'bank_transfer')}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="pm-cheque"
                label="Cheque"
                value="cheque"
                checked={watch('paymentMethod') === 'cheque'}
                onChange={() => setValue('paymentMethod', 'cheque')}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="pm-amember"
                label="aMember"
                value="amember"
                checked={watch('paymentMethod') === 'amember'}
                onChange={() => setValue('paymentMethod', 'amember')}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="pm-direct-debit"
                label="Direct Debit"
                value="direct_debit"
                checked={watch('paymentMethod') === 'direct_debit'}
                onChange={() => setValue('paymentMethod', 'direct_debit')}
              />
              {watch('paymentMethod') !== 'stripe' && (
                <Form.Group className="mt-3">
                  <Form.Label className="fs--1 text-muted">
                    Payment Date (optional — leave blank for today)
                  </Form.Label>
                  <Form.Control
                    type="date"
                    {...methods.register('paymentDate')}
                  />
                </Form.Group>
              )}
            </Card.Body>
          </Card>
          <div ref={sectionRefs[2]}>
            <ADAdvancedSettings
              transactionFee={totalTransactionFee}
              hideTransactionFee={[
                'cash',
                'bank_transfer',
                'cheque',
                'amember',
                'direct_debit'
              ].includes(watch('paymentMethod'))}
              id="settings"
            />
          </div>
          {/* <ADScheduleDonations /> */}
          <LoadingButton
            loading={loading}
            size="lg"
            variant="warning"
            className="w-100 py-3 mt-3 fs-0 fs-md-1"
            onClick={RHFHandleSubmit(handleSubmit)}
          >
            {watch('paymentMethod') === 'cash'
              ? 'Confirm Cash Donation'
              : watch('paymentMethod') === 'bank_transfer'
              ? 'Confirm Bank Transfer Donation'
              : watch('paymentMethod') === 'cheque'
              ? 'Confirm Cheque Donation'
              : 'Save And Continue To Payments'}
          </LoadingButton>
        </FormProvider>
      </Col>
      <Col md={3} className="order-0 order-md-1 d-none d-md-block">
        <Card
          className={'mb-3 sticky-top border shadow-none'}
          style={{ top: stickyStatus ? '5rem' : '0' }}
        >
          <Card.Body>
            <div>
              <Nav className="flex-column" variant="pills">
                {sideNavLinks?.map(item => (
                  <Nav.Link
                    key={item.id}
                    href={item.href}
                    className={`${
                      activeSection === item.id
                        ? 'current custom-nav-link'
                        : 'custom-nav-link'
                    }`}
                    active={activeSection === item.id - 1}
                  >
                    {item.title}
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

DonationConfig.propTypes = {
  onSubmit: PropTypes.func,
  loading: PropTypes.bool
};

export default DonationConfig;
